// GitOps Module Catalog - Approved Building Blocks Only

export const moduleCatalog = [
  // Terraform Modules
  {
    id: 'tf-aws-eks-cluster',
    name: 'AWS EKS Cluster',
    type: 'terraform',
    version: '3.2.1',
    latestVersion: '3.2.1',
    description: 'Production-ready EKS cluster with managed node groups',
    maintainer: 'Platform Team',
    verified: true,
    parameters: [
      { name: 'cluster_name', type: 'string', required: true },
      { name: 'kubernetes_version', type: 'string', required: true, allowed: ['1.28', '1.29', '1.30'] },
      { name: 'node_instance_types', type: 'list(string)', required: true },
      { name: 'desired_capacity', type: 'number', required: true, min: 1, max: 100 },
      { name: 'vpc_id', type: 'string', required: true },
      { name: 'subnet_ids', type: 'list(string)', required: true }
    ],
    usageCount: 12,
    lastUsed: '2026-01-10'
  },
  {
    id: 'tf-aws-rds-postgres',
    name: 'AWS RDS PostgreSQL',
    type: 'terraform',
    version: '2.8.0',
    latestVersion: '2.8.0',
    description: 'Managed PostgreSQL with encryption, backups, and monitoring',
    maintainer: 'Data Team',
    verified: true,
    parameters: [
      { name: 'identifier', type: 'string', required: true },
      { name: 'engine_version', type: 'string', required: true, allowed: ['14', '15', '16'] },
      { name: 'instance_class', type: 'string', required: true },
      { name: 'allocated_storage', type: 'number', required: true, min: 20, max: 10000 },
      { name: 'multi_az', type: 'bool', required: false, default: true },
      { name: 'encryption_enabled', type: 'bool', required: false, default: true }
    ],
    usageCount: 8,
    lastUsed: '2026-01-09'
  },
  {
    id: 'tf-aws-elasticache-redis',
    name: 'AWS ElastiCache Redis',
    type: 'terraform',
    version: '1.5.2',
    latestVersion: '1.5.2',
    description: 'Managed Redis cluster with replication and failover',
    maintainer: 'Platform Team',
    verified: true,
    parameters: [
      { name: 'cluster_id', type: 'string', required: true },
      { name: 'node_type', type: 'string', required: true },
      { name: 'num_cache_nodes', type: 'number', required: true, min: 1, max: 6 },
      { name: 'engine_version', type: 'string', required: true },
      { name: 'parameter_group_name', type: 'string', required: false }
    ],
    usageCount: 5,
    lastUsed: '2026-01-08'
  },
  // Helm Charts
  {
    id: 'helm-nginx-ingress',
    name: 'NGINX Ingress Controller',
    type: 'helm',
    version: '4.9.0',
    latestVersion: '4.9.0',
    description: 'Production ingress controller with SSL termination',
    maintainer: 'Platform Team',
    verified: true,
    parameters: [
      { name: 'replicaCount', type: 'number', required: false, default: 2 },
      { name: 'service.type', type: 'string', required: false, allowed: ['LoadBalancer', 'NodePort'] },
      { name: 'resources.limits.cpu', type: 'string', required: false },
      { name: 'resources.limits.memory', type: 'string', required: false }
    ],
    usageCount: 15,
    lastUsed: '2026-01-11'
  },
  {
    id: 'helm-prometheus-stack',
    name: 'Prometheus Stack',
    type: 'helm',
    version: '55.5.0',
    latestVersion: '55.5.0',
    description: 'Full monitoring stack with Prometheus, Grafana, Alertmanager',
    maintainer: 'Platform Team',
    verified: true,
    parameters: [
      { name: 'prometheus.retention', type: 'string', required: false, default: '15d' },
      { name: 'grafana.enabled', type: 'bool', required: false, default: true },
      { name: 'alertmanager.enabled', type: 'bool', required: false, default: true }
    ],
    usageCount: 10,
    lastUsed: '2026-01-10'
  },
  {
    id: 'helm-cert-manager',
    name: 'Cert Manager',
    type: 'helm',
    version: '1.14.0',
    latestVersion: '1.14.0',
    description: 'Automated TLS certificate management',
    maintainer: 'Security Team',
    verified: true,
    parameters: [
      { name: 'installCRDs', type: 'bool', required: false, default: true },
      { name: 'replicaCount', type: 'number', required: false, default: 1 }
    ],
    usageCount: 12,
    lastUsed: '2026-01-11'
  },
  // Config Roles
  {
    id: 'config-okta-user',
    name: 'Okta User Configuration',
    type: 'config',
    version: '1.2.0',
    latestVersion: '1.2.0',
    description: 'Standard Okta user provisioning with group assignments',
    maintainer: 'Identity Team',
    verified: true,
    parameters: [
      { name: 'email', type: 'string', required: true },
      { name: 'firstName', type: 'string', required: true },
      { name: 'lastName', type: 'string', required: true },
      { name: 'groups', type: 'list(string)', required: true },
      { name: 'status', type: 'string', required: false, allowed: ['ACTIVE', 'STAGED', 'SUSPENDED'] }
    ],
    usageCount: 245,
    lastUsed: '2026-01-11'
  },
  {
    id: 'config-github-team',
    name: 'GitHub Team Configuration',
    type: 'config',
    version: '1.0.5',
    latestVersion: '1.0.5',
    description: 'GitHub team and repository access management',
    maintainer: 'Platform Team',
    verified: true,
    parameters: [
      { name: 'team_name', type: 'string', required: true },
      { name: 'members', type: 'list(string)', required: true },
      { name: 'repositories', type: 'list(object)', required: false },
      { name: 'privacy', type: 'string', required: false, allowed: ['closed', 'secret'] }
    ],
    usageCount: 34,
    lastUsed: '2026-01-10'
  },
  // Policy Packs
  {
    id: 'policy-security-baseline',
    name: 'Security Baseline',
    type: 'policy',
    version: '2.1.0',
    latestVersion: '2.1.0',
    description: 'Core security policies: encryption, access controls, audit logging',
    maintainer: 'Security Team',
    verified: true,
    parameters: [
      { name: 'enforce_encryption', type: 'bool', required: false, default: true },
      { name: 'require_mfa', type: 'bool', required: false, default: true },
      { name: 'audit_retention_days', type: 'number', required: false, default: 90 }
    ],
    usageCount: 50,
    lastUsed: '2026-01-11'
  },
  {
    id: 'policy-cost-controls',
    name: 'Cost Controls',
    type: 'policy',
    version: '1.3.0',
    latestVersion: '1.3.0',
    description: 'Resource quotas, instance type restrictions, cleanup policies',
    maintainer: 'Finance Team',
    verified: true,
    parameters: [
      { name: 'max_monthly_spend', type: 'number', required: false },
      { name: 'allowed_instance_types', type: 'list(string)', required: false },
      { name: 'require_cost_tags', type: 'bool', required: false, default: true }
    ],
    usageCount: 25,
    lastUsed: '2026-01-09'
  },
  // Workflow Templates
  {
    id: 'workflow-user-offboarding',
    name: 'User Offboarding',
    type: 'workflow',
    version: '2.0.0',
    latestVersion: '2.0.0',
    description: 'Standard offboarding across all identity systems',
    maintainer: 'Identity Team',
    verified: true,
    parameters: [
      { name: 'user_email', type: 'string', required: true },
      { name: 'transfer_owner', type: 'string', required: true },
      { name: 'retention_days', type: 'number', required: false, default: 30 },
      { name: 'notify_manager', type: 'bool', required: false, default: true }
    ],
    usageCount: 156,
    lastUsed: '2026-01-10'
  },
  {
    id: 'workflow-incident-quarantine',
    name: 'Incident Quarantine',
    type: 'workflow',
    version: '1.1.0',
    latestVersion: '1.1.0',
    description: 'Emergency device isolation and access revocation',
    maintainer: 'Security Team',
    verified: true,
    parameters: [
      { name: 'device_id', type: 'string', required: true },
      { name: 'reason', type: 'string', required: true },
      { name: 'network_isolate', type: 'bool', required: false, default: true },
      { name: 'revoke_tokens', type: 'bool', required: false, default: true }
    ],
    usageCount: 23,
    lastUsed: '2026-01-07'
  }
];

export const moduleTypes = [
  { id: 'terraform', label: 'Terraform Module', icon: 'layers', count: 3 },
  { id: 'helm', label: 'Helm Chart', icon: 'box', count: 3 },
  { id: 'config', label: 'Config Role', icon: 'settings', count: 2 },
  { id: 'policy', label: 'Policy Pack', icon: 'shield', count: 2 },
  { id: 'workflow', label: 'Workflow Template', icon: 'git-branch', count: 2 }
];